import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Camera, CheckCircle2, LocateFixed, QrCode, RefreshCw, Save, WifiOff } from 'lucide-react'
import { api } from '../lib/api'
import { photoError } from '../lib/photo'
import { attachmentFromFile, mutationId, queueMutation } from '../lib/offline'
import { QrScanner } from '../components/QrScanner'

type Position = { latitude: number; longitude: number; accuracy: number }

export function FieldActivateLive() {
  const [campaigns, setCampaigns] = useState<any[]>([])
  const [sites, setSites] = useState<any[]>([])
  const [plots, setPlots] = useState<any[]>([])
  const [species, setSpecies] = useState<any[]>([])
  const [campaignId, setCampaignId] = useState('')
  const [siteId, setSiteId] = useState('')
  const [tagCode, setTagCode] = useState('')
  const [position, setPosition] = useState<Position | null>(null)
  const [locating, setLocating] = useState(false)
  const [scanner, setScanner] = useState(false)
  const [photo, setPhoto] = useState<File | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<any>(null)
  const [busy, setBusy] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const loadOptions = async () => {
    setLoading(true)
    try {
      const [campaignRows, siteRows, plotRows, speciesRows] = await Promise.all([api.campaigns(), api.sites(), api.plots(), api.species()])
      setCampaigns(campaignRows)
      setSites(siteRows)
      setPlots(plotRows)
      setSpecies(speciesRows)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void loadOptions() }, [])

  const visibleSites = useMemo(
    () => campaignId ? sites.filter((site) => !site.campaign_id || site.campaign_id === campaignId) : sites,
    [campaignId, sites],
  )
  const visiblePlots = useMemo(
    () => siteId ? plots.filter((plot) => plot.site_id === siteId) : [],
    [siteId, plots],
  )

  const locate = () => {
    if (!navigator.geolocation) {
      setError('This device does not expose location services to the browser.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPosition({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy })
        setLocating(false)
      },
      (e) => {
        setError(e.message)
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 5_000 },
    )
  }

  function choosePhoto(input: HTMLInputElement) {
    const file = input.files?.[0]
    const issue = file ? photoError(file) : ''
    input.setCustomValidity(issue)
    setPhoto(issue ? null : file || null)
    if (issue) input.reportValidity()
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (photo) {
      const issue = photoError(photo)
      if (issue) { setError(issue); return }
    }
    setBusy(true)
    setError('')
    setSuccess(null)

    const form = new FormData(event.currentTarget)
    const id = mutationId()
    const payload = {
      tagCode: tagCode.trim().toUpperCase(),
      campaignId,
      siteId,
      plotId: form.get('plotId') || null,
      species: form.get('species'),
      planter: form.get('planter') || null,
      publicAttribution: form.get('publicAttribution') === 'on',
      initialHeightCm: Number(form.get('height')),
      location: position,
      baselinePhotoName: photo?.name || 'baseline-photo.jpg',
      plantedAt: new Date().toISOString(),
    }

    if (!campaignId || !siteId) {
      setError('Choose the campaign and plantation site.')
      setBusy(false)
      return
    }
    if (!position) {
      setError('Capture GPS before activation.')
      setBusy(false)
      return
    }
    if (!photo) {
      setError('A baseline tree photograph is required.')
      setBusy(false)
      return
    }

    const attachment = attachmentFromFile(photo, 'baseline')

    try {
      const response = await fetch('/api/field/activate', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': id },
        body: JSON.stringify(payload),
      })
      const result = await response.json()
      if (!response.ok) throw Object.assign(new Error(result.error || `Activation failed (${response.status})`), { status: response.status })

      try {
        await api.uploadMedia(photo, { observationId: result.observationId, treeId: result.treeId, purpose: 'baseline' })
      } catch (mediaError) {
        await queueMutation('activate_tree', payload, id, attachment)
        setError(`Tree activated. The baseline photo is safely queued for retry: ${mediaError instanceof Error ? mediaError.message : String(mediaError)}`)
      }

      setSuccess(result)
      formRef.current?.reset()
      setTagCode('')
      setPhoto(null)
      setPosition(null)
    } catch (err: any) {
      if (!navigator.onLine || err instanceof TypeError) {
        await queueMutation('activate_tree', payload, id, attachment)
        setSuccess({ queued: true, tagCode: payload.tagCode })
        setError('Activation and its baseline photo are saved on this device and will sync automatically when connectivity returns.')
      } else {
        setError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page-stack">
      <section className="page-header-row">
        <div><h2>Activate a tree</h2><p>Activation requires a physical tag, GPS and a baseline photo.</p></div>
        <div className="hero-actions">{!navigator.onLine && <span className="button button--secondary"><WifiOff size={16} /> Offline mode</span>}<button className="button button--secondary" onClick={() => void loadOptions()} disabled={loading}><RefreshCw size={16} /> Refresh</button></div>
      </section>

      {error && <section className="panel form-feedback" role="alert"><strong>{success?.queued ? 'Saved safely' : 'Action needs attention'}</strong><p>{error}</p></section>}
      {success && !success.queued && <section className="panel form-feedback"><div style={{ display: 'flex', gap: 12, alignItems: 'center' }}><CheckCircle2 size={28} /><div><strong>{success.tagCode} activated</strong><p>Tree and planting spot were created. <a href={success.publicPath}>Open public passport</a></p></div></div></section>}

      <form ref={formRef} className="panel" onSubmit={submit} aria-busy={loading}>
        <div className="panel-heading"><div><span className="eyebrow">1 · TAG</span><h3>Scan or enter physical tag</h3></div><button type="button" className="button button--secondary" onClick={() => setScanner(true)} disabled={loading || busy}><QrCode size={16} /> Scan QR</button></div>
        {loading && <p role="status" style={{ padding: '0 19px', margin: 0 }}>Loading campaigns, sites and species…</p>}
        <label className="field-label"><span>Tag code</span><input required value={tagCode} onChange={(event) => setTagCode(event.target.value.toUpperCase())} placeholder="TREE26-ABC12345" autoCapitalize="characters" disabled={loading || busy} /></label>

        <div className="dashboard-grid">
          <label className="field-label"><span>Campaign</span><select required value={campaignId} onChange={(event) => { setCampaignId(event.target.value); setSiteId('') }} disabled={loading || busy}><option value="">Choose campaign</option>{campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}</select></label>
          <label className="field-label"><span>Site</span><select required value={siteId} onChange={(event) => setSiteId(event.target.value)} disabled={loading || busy || !campaignId}><option value="">Choose site</option>{visibleSites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
        </div>

        <div className="dashboard-grid">
          <label className="field-label"><span>Plot <em>optional</em></span><select name="plotId" disabled={loading || busy || !siteId}><option value="">No plot</option>{visiblePlots.map((plot) => <option key={plot.id} value={plot.id}>{plot.name} · {plot.monitoring_mode}</option>)}</select></label>
          <label className="field-label"><span>Species</span><input name="species" required list="species-list" placeholder="Cedrus deodara" disabled={loading || busy} /><datalist id="species-list">{species.map((sp) => <option key={sp.id} value={sp.scientific_name}>{sp.common_name}</option>)}</datalist></label>
        </div>

        <label className="field-label"><span>Initial height (cm)</span><input name="height" type="number" min="1" max="5000" step="0.1" required disabled={loading || busy} /></label>

        <div className="panel-heading"><div><span className="eyebrow">2 · EVIDENCE</span><h3>Baseline photo and location</h3></div></div>
        <label className="photo-picker" style={{ cursor: loading || busy ? 'not-allowed' : 'pointer' }}><input className="field-photo-input" aria-label="Take baseline tree photo" type="file" accept="image/*" capture="environment" onChange={(event) => choosePhoto(event.currentTarget)} disabled={loading || busy} /><Camera size={22} /><span><strong>{photo?.name || 'Take baseline tree photo'}</strong><small>Frame the sapling, guard and immediate planting area.</small></span></label>
        <button type="button" className="button button--secondary" onClick={locate} disabled={locating || loading || busy}><LocateFixed size={16} />{locating ? ' Locating…' : position ? ` GPS ±${Math.round(position.accuracy)}m` : ' Capture GPS'}</button>
        {position && position.accuracy > 50 && <p className="passport-smallprint">GPS accuracy is ±{Math.round(position.accuracy)} m. For closely spaced trees, move to an open-sky position and capture again if possible.</p>}

        <div className="panel-heading" style={{ marginTop: 20 }}><div><span className="eyebrow">3 · ATTRIBUTION</span><h3>Recognize the planter</h3></div></div>
        <label className="field-label"><span>Planter / group <em>optional</em></span><input name="planter" placeholder="Participant, school or team" disabled={loading || busy} /></label>
        <label style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13 }}><input name="publicAttribution" type="checkbox" disabled={loading || busy} /> Show this attribution on the public passport</label>
        <button disabled={busy || loading} className="button button--primary button--wide" style={{ marginTop: 20 }}><Save size={17} />{busy ? ' Saving…' : ' Activate tree passport'}</button>
      </form>

      {scanner && <QrScanner onCode={setTagCode} onClose={() => setScanner(false)} />}
    </div>
  )
}



